import { useEffect, useState, useRef } from "react";
import { Check, ChevronLeft, ChevronRight, Circle } from "lucide-react";
import type { SetupView } from "@proof/core";
import { useWorkspaceTransport } from "./WorkspaceContext";
import { Button, Control } from "./components";

type Step = SetupView["steps"][number];
/** Plain-language help and the page that customizes each setup item. */
const guide: Record<
  string,
  { about: string; module?: string; siteNote?: string }
> = {
  "tenant-context": {
    about: "The name people see for this workspace.",
  },
  "managed-font": {
    about: "A regular (400) font that every document can rely on.",
    module: "library-fonts",
    siteNote: "Uses the site's standard fonts.",
  },
  "page-default": {
    about: "The page size new documents start with.",
    module: "library-catalog",
    siteNote: "Letter-size pages from the site's starter defaults.",
  },
  "rgb-output": {
    about: "How on-screen and digital files are produced.",
    module: "library-outputs",
    siteNote: "The site's standard RGB PDF and image output.",
  },
  production: {
    about: "Bleed, slug and printer's marks for print-ready pages.",
    module: "library-bleed",
    siteNote: "No bleed unless a project asks for it.",
  },
  datasets: {
    about: "Lists and tables that feed dropdowns and lookups.",
    module: "library-datasets",
    siteNote: "Projects bring their own lists.",
  },
  validation: {
    about: "Reusable rules that check what people type.",
    module: "library-mapping",
    siteNote: "Each field's own validation applies.",
  },
  format: {
    about: "Reusable formatting such as capitalization or prefixes.",
    module: "library-mapping",
    siteNote: "Each field's own formatting applies.",
  },
  imposition: {
    about: "Sheet sizes and imposition for print production.",
    module: "library-sheets",
    siteNote: "One document per sheet.",
  },
  "first-document": {
    about: "Create or import the first document people will personalize.",
    module: "projects",
    siteNote: "Start from the demo documents.",
  },
  "draft-retention": {
    about: "How many saved draft versions each document keeps.",
    module: "setup",
    siteNote: "Keep the site default of 20 versions.",
  },
};
const status = (step: Step) =>
  step.state === "ready"
    ? "Ready"
    : step.state === "skipped"
      ? "Using site configuration"
      : "Needs a choice";

/**
 * Guided workspace setup: one item at a time, each defaulting to the site
 * configuration with the option to use (or create) a company configuration.
 * Everything stays changeable later; advanced pages open from each step.
 */
export function TenantSetup({
  view,
  onChange,
  onGo,
  onUseDefaults,
  defaultsLabel = "Use current document as defaults",
}: {
  defaultsLabel?: string;
  view: SetupView;
  onChange: (v: SetupView) => void;
  onGo: (route: string) => void;
  onUseDefaults: (signal?: AbortSignal) => Promise<void>;
}) {
  const { api } = useWorkspaceTransport();
  const [name, setName] = useState(view.envelope.displayName),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [index, setIndex] = useState(() =>
      Math.max(
        0,
        view.steps.findIndex(
          (step) => step.state !== "ready" && step.state !== "skipped",
        ),
      ),
    );
  useEffect(
    () => setName(view.envelope.displayName),
    [view.envelope.displayName],
  );
  const saving = useRef(false);
  const failure = (e: unknown) =>
    e instanceof DOMException && ["TimeoutError", "AbortError"].includes(e.name)
      ? "Saving timed out. Recheck readiness before retrying; your existing settings are retained."
      : (e as Error).message;
  async function change(action: object) {
    if (saving.current) return;
    saving.current = true;
    setBusy(true);
    setError("");
    try {
      onChange(
        await api<SetupView>("/setup", {
          method: "POST",
          signal: AbortSignal.timeout(30000),
          body: JSON.stringify({
            ...action,
            expectedRevision: view.envelope.revision,
          }),
        }),
      );
    } catch (e) {
      setError(failure(e));
      try {
        onChange(
          await api<SetupView>("/setup", { signal: AbortSignal.timeout(5000) }),
        );
      } catch {
        /* Keep the last view available. */
      }
    } finally {
      saving.current = false;
      setBusy(false);
    }
  }
  const useSiteDefaults = () => {
    if (saving.current) return;
    saving.current = true;
    setBusy(true);
    setError("");
    const signal = AbortSignal.timeout(30000);
    void Promise.race([
      Promise.resolve().then(() => onUseDefaults(signal)),
      new Promise<never>((_, reject) =>
        signal.addEventListener("abort", () => reject(signal.reason), {
          once: true,
        }),
      ),
    ])
      .then(() => api<SetupView>("/setup", { signal }))
      .then(onChange)
      .catch((e) => setError(failure(e)))
      .finally(() => {
        saving.current = false;
        setBusy(false);
      });
  };
  const reviewing = index >= view.steps.length;
  const step = view.steps[Math.min(index, view.steps.length - 1)];
  const info = guide[step?.id] || { about: "" };
  // Required resource defaults come from the site's starter defaults;
  // optional items simply inherit the site configuration.
  const siteChoice = () => {
    if (!step) return;
    if (step.required) {
      if (step.state !== "ready") useSiteDefaults();
      return;
    }
    if (step.state !== "skipped")
      void change({ action: "skip", stepId: step.id, skipped: true });
  };
  const companyChoice = () => {
    if (!step) return;
    if (!step.required && step.state === "skipped")
      void change({ action: "skip", stepId: step.id, skipped: false });
    if (info.module && info.module !== "setup") onGo(info.module);
  };
  const doneCount = view.steps.filter(
    (row) => row.state === "ready" || row.state === "skipped",
  ).length;
  return (
    <section className="tenant-setup setup-wizard" aria-busy={busy}>
      <div className="page-heading">
        <div>
          <span className="eyebrow">WORKSPACE</span>
          <h1>Workspace setup</h1>
          <p>
            Every item starts from the site configuration. Keep it, or switch an
            item to your company's own configuration — you can change any of
            this later.
          </p>
        </div>
        <div className="setup-progress" aria-label="Setup progress">
          <strong>
            {doneCount} / {view.steps.length}
          </strong>
          <span>items ready</span>
        </div>
      </div>
      {busy && <p role="status">Saving configuration…</p>}
      {error && <p role="alert">{error}</p>}
      <div className="setup-wizard-body">
        <ol className="setup-rail" aria-label="Setup items">
          {view.steps.map((row, i) => (
            <li key={row.id} data-setup-step={row.id}>
              <button
                type="button"
                aria-current={i === index ? "step" : undefined}
                className={i === index ? "active" : ""}
                onClick={() => setIndex(i)}
              >
                {row.state === "ready" || row.state === "skipped" ? (
                  <Check size={14} className="done" aria-hidden="true" />
                ) : (
                  <Circle size={14} aria-hidden="true" />
                )}
                <span>
                  {row.title}
                  <small>
                    {row.required ? "Required" : "Optional"} · {status(row)}
                  </small>
                </span>
              </button>
            </li>
          ))}
          <li>
            <button
              type="button"
              aria-current={reviewing ? "step" : undefined}
              className={reviewing ? "active" : ""}
              onClick={() => setIndex(view.steps.length)}
            >
              <Check size={14} aria-hidden="true" />
              <span>
                Review & finish
                <small>{view.ready ? "Ready to complete" : "Items left"}</small>
              </span>
            </button>
          </li>
        </ol>
        <div className="setup-pane panel padded">
          {reviewing ? (
            <>
              <h2>Review & finish</h2>
              <ul className="setup-review">
                {view.steps.map((row) => (
                  <li key={row.id}>
                    <span>{row.title}</span>
                    <strong>{status(row)}</strong>
                  </li>
                ))}
              </ul>
              <div className="setup-actions">
                <Button
                  disabled={busy}
                  onClick={() =>
                    void api<SetupView>("/setup", {
                      signal: AbortSignal.timeout(10000),
                    })
                      .then(onChange)
                      .catch((e) => setError(e.message))
                  }
                >
                  Recheck readiness
                </Button>
                <Button
                  kind="dark"
                  disabled={busy || !view.ready}
                  onClick={() => void change({ action: "complete" })}
                >
                  {view.envelope.completedAt
                    ? "Confirm setup"
                    : "Complete setup"}
                </Button>
              </div>
              {view.envelope.completedAt && (
                <p className="muted">
                  Setup completed on{" "}
                  {new Date(view.envelope.completedAt).toLocaleDateString()}.
                  Readiness is checked against current workspace resources.
                </p>
              )}
            </>
          ) : (
            step && (
              <>
                <span className="eyebrow">
                  STEP {index + 1} OF {view.steps.length} ·{" "}
                  {step.required ? "REQUIRED" : "OPTIONAL"}
                </span>
                <h2>{step.title}</h2>
                <p>{info.about}</p>
                {step.id === "tenant-context" ? (
                  <div className="setup-details">
                    <Control label="Workspace display name">
                      <input
                        value={name}
                        maxLength={200}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </Control>
                    <Button
                      kind="primary"
                      disabled={
                        busy ||
                        !name.trim() ||
                        name.trim() === view.envelope.displayName
                      }
                      onClick={() =>
                        void change({ action: "details", displayName: name })
                      }
                    >
                      Save workspace details
                    </Button>
                  </div>
                ) : (
                  <div
                    className="setup-choices"
                    role="radiogroup"
                    aria-label={`${step.title} configuration`}
                  >
                    <button
                      type="button"
                      role="radio"
                      aria-checked={
                        step.state === "skipped" ||
                        (step.required && step.state === "ready")
                      }
                      className="setup-choice"
                      disabled={busy}
                      onClick={siteChoice}
                    >
                      <strong>Use site configuration</strong>
                      <small>
                        {info.siteNote ||
                          "The standard settings for this site."}
                        {step.required ? ` ${defaultsLabel}.` : ""}
                      </small>
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={!step.required && step.state === "ready"}
                      className="setup-choice"
                      disabled={busy || !info.module || info.module === "setup"}
                      onClick={companyChoice}
                    >
                      <strong>Use company configuration</strong>
                      <small>
                        Choose or create your company's own{" "}
                        {step.title.toLowerCase()}. Opens the advanced settings.
                      </small>
                    </button>
                  </div>
                )}
                <p className="setup-status">
                  Status: <strong>{status(step)}</strong>
                </p>
                <details className="setup-advanced">
                  <summary>Advanced</summary>
                  <div className="setup-actions">
                    {info.module && info.module !== "setup" && (
                      <Button onClick={() => onGo(info.module!)}>
                        Open {step.title.toLowerCase()} settings
                      </Button>
                    )}
                    {!step.required && (
                      <Button
                        disabled={busy}
                        onClick={() =>
                          void change({
                            action: "skip",
                            stepId: step.id,
                            skipped: step.state !== "skipped",
                          })
                        }
                      >
                        {step.state === "skipped" ? "Restore" : "Skip"}{" "}
                        {step.title.toLowerCase()}
                      </Button>
                    )}
                    <Button disabled={busy} onClick={useSiteDefaults}>
                      {defaultsLabel}
                    </Button>
                  </div>
                </details>
              </>
            )
          )}
          <div className="setup-nav">
            <Button disabled={index === 0} onClick={() => setIndex(index - 1)}>
              <ChevronLeft size={14} /> Back
            </Button>
            {!reviewing && (
              <Button kind="primary" onClick={() => setIndex(index + 1)}>
                {index === view.steps.length - 1 ? "Review" : "Next"}{" "}
                <ChevronRight size={14} />
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
